import { useMemo } from 'react';

import { AnnouncementBar } from '@/components/AnnouncementBar';
import { Button } from '@/components/Button';
import { Carousel } from '@/components/Carousel/Carousel';
import { DestinationInsight } from '@/components/DestinationInsight';
import { Header } from '@/components/Header';
import { HTMLParagraph } from '@/components/HTMLParagraph';
import { NameTag } from '@/components/NameTag';
import { Navbar } from '@/components/Navbar';
import { Spacer } from '@/components/Spacer';
import { useItineraryContext } from '@/context/ItineraryContext';
import { CenteredText, DualSection, EnforcePageWidth, StyledColoredBackground } from '@/layout/PageLayout';
import { getHeaderImage, getImages } from '@/utils/getImage';

import {
  StyledCarouselImage,
  StyledInsightsCollection,
  StyledLocalQuote,
  StyledTightIntroduction,
  StyledCenteredImage,
  StyledBlockImage,
} from './styles';

function DestinationPage() {
  const { data } = useItineraryContext();
  const destination = data?.Destination;

  const mainContent = useMemo(
    () => destination?.Content.find((content) => content.type.toLowerCase() === 'main'),
    [destination?.Content],
  );

  const mainHeaderImage = useMemo(() => (mainContent ? getHeaderImage(mainContent) : ''), [mainContent]);

  const secondaryContent = useMemo(
    () => destination?.Content.find((content) => content.type.toLowerCase() === 'secondary'),
    [destination?.Content],
  );

  const tipContent = useMemo(
    () => destination?.Content.find((content) => content.type.toLowerCase() === 'tip'),
    [destination?.Content],
  );

  const images = useMemo(() => (mainContent ? getImages(mainContent) : []), [mainContent]);

  const headerImage = useMemo(() => (secondaryContent ? getHeaderImage(secondaryContent) : ''), [secondaryContent]);

  const destinationSummaryItems = useMemo(
    () =>
      destination?.Content.filter((content) => content.type.toLowerCase() === 'destinationsummaryitem').map(
        (content) => ({
          name: content.header ?? '',
          value: content.subHeader ?? '',
          paragraph: content.paragraph ?? '',
        }),
      ),
    [destination?.Content],
  );

  const localReviewer = useMemo(
    () => destination?.Content.find((content) => content.type.toLowerCase() === 'localreviewer'),
    [destination?.Content],
  );

  if (!destination) {
    return null;
  }

  return (
    <>
      <AnnouncementBar />
      <Navbar variant="primary" />
      <Header title={destination?.name} subTitle={mainContent?.header} backgroundUrl={mainHeaderImage} />
      <EnforcePageWidth>
        <Spacer size="xl" />
        <DualSection>
          <h2>{mainContent?.subHeader}</h2>
          <HTMLParagraph pt={4} size="lg" dangerousHTML={mainContent?.paragraph ?? ''} />
        </DualSection>
      </EnforcePageWidth>
      <StyledColoredBackground>
        <Carousel
          showOverview={false}
          alignControls="right"
          startAtIndex={2}
          maxCardHeightPx={500}
          cardWrapperHeightPx={600}
          cards={images.map((image) => ({
            id: image.id,
            element: <StyledCarouselImage src={image.path} alt={`Afbeelding ${image.id}`} />,
          }))}
        />
      </StyledColoredBackground>
      <Spacer size="2xl" />
      <CenteredText>
        <h1>{destination.name} briefly</h1>
      </CenteredText>
      <Spacer size="md" />
      <EnforcePageWidth>
        <StyledInsightsCollection>
          {destinationSummaryItems?.map((insight) => <DestinationInsight key={insight.name} insight={insight} />)}
        </StyledInsightsCollection>
      </EnforcePageWidth>
      <Spacer size="2xl" />

      <StyledColoredBackground>
        <CenteredText>
          <h1>Residents have their say</h1>
        </CenteredText>
        <Spacer size="md" />
        <StyledLocalQuote>
          <HTMLParagraph
            size="2xl"
            weight={500}
            textAlign="center"
            dangerousHTML={`&quot;${localReviewer?.paragraph}&quot;`}
          />
          <Spacer size="xl" />

          <NameTag
            margin="0 auto"
            name={localReviewer?.header ?? 'resident'}
            subtitle={localReviewer?.subHeader ?? 'resident'}
            imageUrl={
              localReviewer?.RichContent?.filter((content) => content.type.toLowerCase() === 'image')[0]?.path || ''
            }
          />
        </StyledLocalQuote>
      </StyledColoredBackground>
      <Spacer size="xl" />
      <StyledTightIntroduction>
        <DualSection>
          <h2>{secondaryContent?.header}</h2>
          <HTMLParagraph pt={4} size="lg" dangerousHTML={secondaryContent?.paragraph ?? ''} />
        </DualSection>
        <EnforcePageWidth>
          <StyledCenteredImage width="100%" src={headerImage} />
        </EnforcePageWidth>
        <Spacer size="2xl" />
        {/* <CenteredText>
          <h3>Unforgettable trips. That's what we do it for.</h3>
        </CenteredText>
        <p>Reviews using a tripadvisor widget</p> */}
        <Spacer size="2xl" />
        <DualSection>
          <StyledBlockImage
            src={tipContent?.RichContent?.filter((content) => content.subType?.toLowerCase() === 'header')[0]?.path}
          />
          <div>
            <h1>{tipContent?.header}</h1>
            <HTMLParagraph pt={4} size="lg" dangerousHTML={tipContent?.paragraph ?? ''} />
            <Button mt={8} variant="secondary">
              Read our story
            </Button>
          </div>
        </DualSection>
      </StyledTightIntroduction>
    </>
  );
}

export { DestinationPage };
